/* eslint-disable @typescript-eslint/no-explicit-any */
// src/components/CalendarFeature.tsx
import React, { useState, useEffect, useRef } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import timeGridPlugin from "@fullcalendar/timegrid";
import listPlugin from "@fullcalendar/list";
import firebase from "firebase/app";
import "firebase/firestore";
import "firebase/auth";
import { FollowUp } from "../../../interfaces/FollowUp";
import { Collections } from "../../../types/collection";
import FollowUpCard from "../components/FollowUpCard";
import { Toast } from "primereact/toast";
import { useUserContext } from "../../../contexts/user.context";
import { Dialog } from "primereact/dialog";
import { captureException } from "@sentry/react";
import { analytics } from "../../firebase/firebase-config";
import { motion, AnimatePresence } from "framer-motion";
import { FaCalendar, FaPlus, FaTimes, FaClock, FaTrash, FaHome } from "react-icons/fa";
import { IoCalendarOutline } from "react-icons/io5";
import { BsCalendarCheck, BsCalendarEvent } from "react-icons/bs";
import { confirmDialog } from "primereact/confirmdialog";
import { useNavigate } from "react-router-dom";

const CalendarFeature: React.FC = () => {
  analytics.logEvent("page_view", {
    page_title: "calendar",
  });

  const toast = useRef<Toast>(null);
  const calendarRef = useRef<any>(null);

  const { user, reloadUserData } = useUserContext();
  const [activeView, setActiveView] = useState("dayGridMonth");

  const [events, setEvents] = useState<any[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState<{
    notes: string;
    date: Date | null;
    sessionRole: string;
    sessionTitle: string;
    sessionId: string;
    runId: string;
  }>({
    notes: "",
    date: null,
    sessionRole: "",
    sessionTitle: "",
    sessionId: "",
    runId: "",
  });

  const [selectedFollowUp, setSelectedFollowUpEvent] = useState<FollowUp | undefined>();
  const [showFollowUpEvent, toggleShowFollowUpEvent] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    reloadUserData();
  }, [reloadUserData]);

  // Fetch events from Firestore
  useEffect(() => {
    const unsubscribe = firebase
      .firestore()
      .collection(Collections.FollowUps)
      .doc(firebase.auth().currentUser?.uid)
      .collection("list")
      .orderBy("date", "asc")
      .onSnapshot((querySnapshot) => {
        const fetchedEvents: any[] = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data() as FollowUp;
          fetchedEvents.push({
            id: doc.id,
            title: data.sessionTitle || "No Title",
            start: data.date.toDate(),
            extendedProps: data,
            allDay: false,
            className: "calendar-event",
            backgroundColor: getEventColor(data),
            borderColor: getEventColor(data),
          });
        });
        setEvents(fetchedEvents);
      });

    return () => unsubscribe();
  }, []);

  // Color generator based on some property of the event
  const getEventColor = (followUp: FollowUp) => {
    // Simple function that returns a color based on the first character of the title
    const colors = [
      "#4f46e5", // indigo
      "#0ea5e9", // sky
      "#8b5cf6", // violet
      "#f97316", // orange
      "#06b6d4", // cyan
      "#ec4899", // pink
    ];

    if (!followUp.sessionTitle) return colors[0];
    const charCode = followUp.sessionTitle.charCodeAt(0);
    return colors[charCode % colors.length];
  };

  // Handle form input changes
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle <input type="datetime-local"> changes
  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.value) {
      setFormData((prev) => ({ ...prev, date: null }));
      return;
    }
    // Convert the datetime-local string to a Date object
    const selectedDate = new Date(e.target.value);
    setFormData((prev) => ({ ...prev, date: selectedDate }));
  };

  // Submit new event to Firestore
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate subscription
    if (!user?.subscription.emailReminder) {
      toast.current?.show({
        severity: "warn",
        summary: "📅 Follow-ups Not Available",
        detail: "Email follow-ups and reminders are available on Basic plans and above. Upgrade to stay on track with your practice!",
        life: 4000,
      });
      return;
    }

    // Validate that a date has been selected
    if (!formData.date) {
      showToast("error", "Please select a date");
      return;
    }
    // Validate that the selected date is in the future
    if (formData.date < new Date()) {
      showToast("error", "Please select a future date");
      return;
    }
    // Validate session title
    if (!formData.sessionTitle.trim()) {
      showToast("error", "Please enter a session title");
      return;
    }

    const newFollowUpId = firebase
      .firestore()
      .collection(Collections.FollowUps)
      .doc()
      .id;

    const newEvent: Omit<FollowUp, "id"> = {
      notes: formData.notes,
      date: firebase.firestore.Timestamp.fromDate(formData.date),
      createdAt: firebase.firestore.Timestamp.now(),
      sessionRole: null,
      sessionTitle: formData.sessionTitle,
      sessionId: formData.sessionId,
      runId: formData.runId,
    };

    const task = {
      date: firebase.firestore.Timestamp.fromDate(formData.date),
      createdAt: firebase.firestore.Timestamp.fromDate(new Date()),
      type: "calendar",
      user: firebase.auth().currentUser?.uid,
      followUpId: newFollowUpId,
    };

    try {
      // Add to FollowUps collection
      await firebase
        .firestore()
        .collection(Collections.FollowUps)
        .doc(firebase.auth().currentUser?.uid)
        .collection("list")
        .doc(newFollowUpId)
        .set(newEvent);

      // Add a related task
      await firebase.firestore().collection(Collections.Tasks).add(task);

      showToast("success", "Event added successfully!");
      setIsModalOpen(false);
      // Reset form data
      setFormData({
        notes: "",
        date: null,
        sessionRole: "",
        sessionTitle: "",
        sessionId: "",
        runId: "",
      });
    } catch (error) {
      captureException(error);
      showToast("error", "Failed to add event");
    }
  };

  const showToast = (severity: "success" | "error", detail: string) => {
    toast.current?.show({
      severity,
      summary: severity === "success" ? "Success" : "Error",
      detail,
      life: 3000,
    });
  };

  // Handle existing event click -> show "Follow Up Details" dialog
  const handleEventClick = (clickInfo: any) => {
    const data = clickInfo.event.extendedProps;
    const event: FollowUp = {
      ...data,
      id: clickInfo.event.id,
    };
    setSelectedFollowUpEvent(event);
    toggleShowFollowUpEvent(true);
  };

  // Delete follow-up from Firestore
  const deleteFollowUp = async (id?: string) => {
    if (!id) {
      showToast("error", "Invalid event ID");
      return;
    }

    try {
      await firebase
        .firestore()
        .collection(Collections.FollowUps)
        .doc(firebase.auth().currentUser?.uid)
        .collection("list")
        .doc(id)
        .delete();

      showToast("success", "Event deleted successfully!");
      toggleShowFollowUpEvent(false);
    } catch (error) {
      captureException(error);
      showToast("error", "Failed to delete event");
    }
  };

  // Change calendar view
  const handleViewChange = (newView: string) => {
    setActiveView(newView);
    if (calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();
      calendarApi.changeView(newView);
    }
  };

  return (
    <div className="min-h-screen">
      <Toast ref={toast} position="top-right" />

      {/* Top Bar */}
      <motion.header
        className="sticky top-0 z-10 bg-white shadow-md"
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.4 }}
      >
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col sm:flex-row items-center justify-between px-4 py-4">
            <motion.div
              className="flex items-center space-x-3 mb-3 sm:mb-0"
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <FaCalendar className="text-2xl sm:text-3xl text-blue-600" />
              <h1 className="text-blue-600 text-xl sm:text-2xl font-bold">My Calendar</h1>
            </motion.div>

            <div className="flex flex-wrap gap-2 justify-center sm:justify-end w-full sm:w-auto">
              <motion.button
                onClick={() => navigate('/dashboard')}
                className="flex items-center bg-gray-100 text-gray-700 px-3 py-2 rounded-md font-medium hover:bg-gray-200 transition text-sm"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
              >
                <FaHome className="mr-2" />
                <span>Dashboard</span>
              </motion.button>

              <motion.button
                onClick={() => setIsModalOpen(true)}
                className="flex items-center bg-blue-600 text-white px-3 py-2 rounded-md font-semibold shadow-md hover:bg-blue-700 transition text-sm"
                whileHover={{ scale: 1.03, boxShadow: "0 4px 8px rgba(0,0,0,0.1)" }}
                whileTap={{ scale: 0.97 }}
              >
                <FaPlus className="mr-2" />
                New Follow-up
              </motion.button>
            </div>
          </div>
        </div>
      </motion.header>

      <div className="lg:px-16 mt-10">
        {/* View Selector Tabs */}
        <motion.div
          className="flex flex-wrap gap-2 my-4 pl-2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          <button
            onClick={() => handleViewChange("dayGridMonth")}
            className={`px-3 py-1.5 rounded-lg flex items-center text-sm font-medium transition-all ${activeView === "dayGridMonth"
              ? "bg-blue-100 text-blue-700 shadow-sm"
              : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
          >
            <BsCalendarEvent className="mr-1.5" />
            Month
          </button>
          <button
            onClick={() => handleViewChange("timeGridWeek")}
            className={`px-3 py-1.5 rounded-lg flex items-center text-sm font-medium transition-all ${activeView === "timeGridWeek"
              ? "bg-blue-100 text-blue-700 shadow-sm"
              : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
          >
            <IoCalendarOutline className="mr-1.5" />
            Week
          </button>
          <button
            onClick={() => handleViewChange("timeGridDay")}
            className={`px-3 py-1.5 rounded-lg flex items-center text-sm font-medium transition-all ${activeView === "timeGridDay"
              ? "bg-blue-100 text-blue-700 shadow-sm"
              : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
          >
            <FaClock className="mr-1.5" />
            Day
          </button>
          <button
            onClick={() => handleViewChange("listMonth")}
            className={`px-3 py-1.5 rounded-lg flex items-center text-sm font-medium transition-all ${activeView === "listMonth"
              ? "bg-blue-100 text-blue-700 shadow-sm"
              : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
          >
            <BsCalendarCheck className="mr-1.5" />
            List
          </button>
        </motion.div>

        {/* Calendar Container */}
        <motion.div
          className="bg-white shadow-lg rounded-xl p-4 mb-8 overflow-hidden border border-gray-100"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.4 }}
        >
          <style>
            {`
            .fc-event {
              border-radius: 4px;
              padding: 2px 4px;
              font-size: 0.85rem;
              cursor: pointer;
              transition: transform 0.2s, box-shadow 0.2s;
            }
            .fc-event:hover {
              transform: translateY(-1px);
              box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
            }
            .fc-toolbar-title {
              font-size: 1.4rem !important;
              font-weight: 600 !important;
              color: #1e40af !important;
            }
            .fc-button-primary {
              background-color: #3b82f6 !important;
              border-color: #3b82f6 !important;
            }
            .fc-button-primary:hover {
              background-color: #2563eb !important;
              border-color: #2563eb !important;
            }
            .fc-day-today {
              background-color: #eff6ff !important;
            }
            .fc th {
              padding: 10px 0 !important;
              font-weight: 500 !important;
            }
            .fc-view-harness {
              min-height: 500px;
            }
          `}
          </style>
          <FullCalendar
            ref={calendarRef}
            plugins={[dayGridPlugin, interactionPlugin, timeGridPlugin, listPlugin]}
            initialView={activeView}
            views={{
              dayGridMonth: { buttonText: 'Month' },
              timeGridWeek: { buttonText: 'Week' },
              timeGridDay: { buttonText: 'Day' },
              listMonth: { buttonText: 'List' }
            }}
            events={events}
            height="auto"
            timeZone="local"
            headerToolbar={{
              left: "prev,next today",
              center: "title",
              right: "",
            }}
            eventClick={handleEventClick}
            eventDisplay="block"
            editable={false}
            selectable={true}
            eventTimeFormat={{
              hour: '2-digit',
              minute: '2-digit',
              meridiem: true
            }}
            eventClassNames={() => ["hover:shadow-md transition-all"]}
          />
        </motion.div>
      </div>

      {/* Follow Up Details Dialog */}
      <Dialog
        visible={showFollowUpEvent}
        onHide={() => toggleShowFollowUpEvent(false)}
        header={
          <div className="flex items-center justify-between w-full pr-2">
            <span className="text-xl font-semibold text-blue-700">Follow up details</span>
          </div>
        }
        style={{ width: "95%", maxWidth: "600px" }}
        className="p-dialog-custom rounded-xl overflow-hidden"
        contentClassName="rounded-lg"
      >
        <AnimatePresence>
          {selectedFollowUp && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
            >
              <FollowUpCard
                key={selectedFollowUp.id}
                followUp={selectedFollowUp}
              />

              <div className="flex justify-end mt-4">
                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={() => {
                    confirmDialog({
                      message: `Delete follow up permanently?`,
                      header: 'Delete Confirmation',
                      icon: 'pi pi-info-circle',
                      defaultFocus: 'reject',
                      acceptClassName: 'bg-red-500 text-white px-2',
                      rejectClassName: 'bg-gray-500 text-white px-2 mr-2',
                      accept: () => deleteFollowUp(selectedFollowUp.id),
                    })
                  }}
                  className="flex items-center px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                >
                  <FaTrash className="mr-2 h-4 w-4" />
                  Delete Follow-up
                </motion.button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </Dialog>

      {/* Add Event Dialog */}
      <Dialog
        visible={isModalOpen}
        onHide={() => setIsModalOpen(false)}
        header={
          <div className="flex items-center justify-between w-full pr-2">
            <span className="text-xl font-semibold text-blue-700">New follow up</span>
            <button
              onClick={() => setIsModalOpen(false)}
              className="p-1.5 rounded-full hover:bg-gray-100 transition-colors"
            >
              <FaTimes className="text-gray-500" />
            </button>
          </div>
        }
        style={{ width: "95%", maxWidth: "600px" }}
        className="p-dialog-custom rounded-xl overflow-hidden"
        contentClassName="rounded-lg"
      >
        <motion.form
          onSubmit={handleSubmit}
          className="space-y-5"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          <div>
            <label
              htmlFor="sessionTitle"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Title
            </label>
            <input
              type="text"
              name="sessionTitle"
              id="sessionTitle"
              value={formData.sessionTitle}
              onChange={handleChange}
              required
              className="block w-full border border-gray-300 rounded-lg shadow-sm p-3 focus:ring-blue-500 focus:border-blue-500 transition-all"
              placeholder="Enter session title"
            />
          </div>

          <div>
            <label
              htmlFor="date"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Date &amp; Time
            </label>
            <input
              type="datetime-local"
              id="date"
              name="date"
              value={
                formData.date
                  ? new Date(formData.date.getTime() - formData.date.getTimezoneOffset() * 60000)
                    .toISOString()
                    .slice(0, 16)
                  : ""
              }
              onChange={handleDateChange}
              className="block w-full border border-gray-300 rounded-lg shadow-sm p-3 focus:ring-blue-500 focus:border-blue-500 transition-all"
              required
              min={
                new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
                  .toISOString()
                  .slice(0, 16)
              }
            />
          </div>

          <div>
            <label
              htmlFor="notes"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Notes
            </label>
            <textarea
              name="notes"
              id="notes"
              value={formData.notes}
              onChange={handleChange}
              rows={3}
              className="block w-full border border-gray-300 rounded-lg shadow-sm p-3 focus:ring-blue-500 focus:border-blue-500 transition-all"
              placeholder="Enter notes"
            ></textarea>
          </div>

          <div className="flex justify-end space-x-3 pt-2">
            <motion.button
              type="button"
              onClick={() => setIsModalOpen(false)}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Cancel
            </motion.button>
            <motion.button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 shadow-sm hover:shadow transition-all"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Save
            </motion.button>
          </div>
        </motion.form>
      </Dialog>
    </div>
  );
};

export default CalendarFeature;
